import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTimeGB } from "../../lib/format";
import { toast } from "sonner";
import {
  FilePlus2, FileText, Send, ShieldCheck, Check, X, Upload, RefreshCw,
  UserCog, Settings, Link2, ScrollText, Activity,
} from "lucide-react";

const ICONS = {
  project_created: FilePlus2,
  status_changed: Activity,
  design_saved: FileText,
  quote_saved: FileText,
  quote_sent: Send,
  quote_emailed: Send,
  quote_customer_signed: Check,
  quote_manager_signed: ShieldCheck,
  quote_rejected: X,
  task_status_changed: Activity,
  document_uploaded: Upload,
  monitoring_synced: RefreshCw,
  assignment_changed: UserCog,
  portal_token_generated: Link2,
  settings_changed: Settings,
};

const ACTION_LABELS = {
  project_created: "Project created",
  status_changed: "Status changed",
  design_saved: "Design updated",
  quote_saved: "Quote saved",
  quote_sent: "Quote sent for approval",
  quote_emailed: "Quote emailed to customer",
  quote_customer_signed: "Customer signed proposal",
  quote_manager_signed: "Manager authorised installation",
  quote_rejected: "Quote declined",
  task_status_changed: "Task status changed",
  document_uploaded: "Document uploaded",
  monitoring_synced: "Monitoring data synced",
  assignment_changed: "Assignment changed",
  portal_token_generated: "Customer portal link created",
};

export default function AuditTab({ projectId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/audit/by-project/${projectId}`)
      .then(r => setEvents(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="muted text-xs font-mono uppercase tracking-widest">Loading audit log…</div>;

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="label">Project history</div>
          <div className="font-display text-2xl font-semibold mt-1">Audit log</div>
          <div className="muted text-xs mt-1">Chronological record of every status change, sign-off, document and update on this project.</div>
        </div>
        <div className="label">{events.length} event{events.length === 1 ? "" : "s"}</div>
      </div>

      <div className="swiss-card-flat" data-testid="audit-list">
        {events.length === 0 ? (
          <div className="muted text-sm text-center py-12">No events yet.</div>
        ) : (
          <ol className="divide-y divide-[var(--line)]">
            {events.map(ev => {
              const Icon = ICONS[ev.action] || ScrollText;
              return (
                <li key={ev.id} className="p-4 flex items-start gap-4" data-testid={`audit-row-${ev.id}`}>
                  <div className="w-8 h-8 bg-[var(--surface)] flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-[var(--ink)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="font-medium text-sm">{ACTION_LABELS[ev.action] || ev.action}</div>
                      <div className="muted text-[11px] font-mono whitespace-nowrap">{fmtDateTimeGB(ev.created_at)}</div>
                    </div>
                    {ev.details && Object.keys(ev.details).length > 0 && (
                      <div className="mt-1 font-mono text-[11px] muted">{renderDetails(ev.details)}</div>
                    )}
                    <div className="mt-1 text-[11px] muted">
                      by <span className="text-[var(--ink)]">{ev.actor_label || (ev.user_email ?? "system")}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}

function renderDetails(d) {
  return Object.entries(d).map(([k, v]) => `${k}: ${formatVal(v)}`).join(" · ");
}
function formatVal(v) {
  if (v == null) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
